import React, { useCallback, useEffect, useState } from 'react';
import Icon from '../components/icons/Icon';
import GitLabAuth from '../auth/GitLabAuth';
import opsStatusService from '../services/OpsStatusService';
import './InternalStatus.css';

/**
 * Hidden ops page: at-a-glance CI/SBOM/security status for the Flow
 * ecosystem's GitLab-hosted repos. Not linked from any nav — reachable only
 * by navigating to /ops/status directly.
 *
 * This is explicitly a fallback to GitLab's own Pipelines/Security Dashboard
 * UI, not a replacement — no auto-polling, no historical trends, manual
 * refresh only. See OpsStatusService.js for how it talks to the BFF's
 * GitLab proxy (cookie-session auth, same as the rest of the app — no
 * GitLab token is ever held in the browser).
 */

// Known Flow component repos, hardcoded per scope. GitLab project paths are
// `${GROUP_PREFIX}/${repo}` — GROUP_PREFIX defaults to this repo's own
// namespace (git remote: git.tools.mia-platform.eu/platform/flow/...) but is
// editable below in case a repo lives under a different group/subgroup.
const REPOS = [
  'mia-flow-fe',
  'mia-flow-bff',
  'adk-be-app',
  'vfs-service',
  'chatbot-widget',
  'mia-desktop',
  'mia-flow-ios',
];

const DEFAULT_GROUP_PREFIX = 'platform/flow';
const GROUP_PREFIX_STORAGE_KEY = 'ops-status-group-prefix';

function loadGroupPrefix() {
  try {
    return localStorage.getItem(GROUP_PREFIX_STORAGE_KEY) || DEFAULT_GROUP_PREFIX;
  } catch {
    return DEFAULT_GROUP_PREFIX;
  }
}

function saveGroupPrefix(value) {
  try {
    localStorage.setItem(GROUP_PREFIX_STORAGE_KEY, value);
  } catch {
    // localStorage unavailable (private browsing, etc.) — non-fatal, just won't persist
  }
}

function statusColor(status) {
  switch (status) {
    case 'success': return '#22C55E';
    case 'failed': return '#EF4444';
    case 'running':
    case 'pending': return '#F59E0B';
    case 'canceled':
    case 'skipped': return '#9CA3AF';
    default: return '#9CA3AF';
  }
}

function formatTimestamp(value) {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleString();
  } catch {
    return value;
  }
}

const SEVERITY_ORDER = ['critical', 'high', 'medium', 'low'];

function RepoCard({ repo, projectId, data, loading, error }) {
  return (
    <div className="ops-status-card">
      <div className="ops-status-card-header">
        <h3 className="ops-status-card-title">{repo}</h3>
        <span className="ops-status-card-project-id">{projectId}</span>
      </div>

      {loading && (
        <div className="ops-status-card-loading">Checking…</div>
      )}

      {!loading && error && (
        <div className="ops-status-card-error">{error}</div>
      )}

      {!loading && !error && data && (
        <>
          <div className="ops-status-section">
            <div className="ops-status-section-label">Latest pipeline</div>
            {data.pipeline ? (
              <div className="ops-status-pipeline-row">
                <span
                  className="ops-status-dot"
                  style={{ backgroundColor: statusColor(data.pipeline.status) }}
                />
                <span className="ops-status-pipeline-status">{data.pipeline.status}</span>
                <span className="ops-status-pipeline-branch">{data.pipeline.ref}</span>
                <a
                  className="ops-status-pipeline-link"
                  href={data.pipeline.web_url}
                  rel="noreferrer"
                  target="_blank"
                >
                  view
                </a>
              </div>
            ) : (
              <div className="ops-status-muted">No pipelines found</div>
            )}
            {data.pipeline && (
              <div className="ops-status-timestamp">
                Updated {formatTimestamp(data.pipeline.updated_at)}
              </div>
            )}
          </div>

          <div className="ops-status-section">
            <div className="ops-status-section-label">Security Dashboard</div>
            {data.vulnerabilities.available ? (
              <div className="ops-status-severity-row">
                {SEVERITY_ORDER.map((severity) => (
                  <span className={`ops-status-severity ops-status-severity-${severity}`} key={severity}>
                    {severity}: {data.vulnerabilities.counts[severity] || 0}
                  </span>
                ))}
              </div>
            ) : (
              <div className="ops-status-muted">Security Dashboard not available on this GitLab tier</div>
            )}
          </div>

          <div className="ops-status-section">
            <div className="ops-status-section-label">SBOM / dependencies</div>
            {data.sbom.available ? (
              <div className="ops-status-muted">{data.sbom.count} components tracked</div>
            ) : (
              <div className="ops-status-muted">Not detected</div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

const InternalStatus = () => {
  const [groupPrefix, setGroupPrefix] = useState(loadGroupPrefix);
  const [gitlabLinked, setGitlabLinked] = useState(null);
  const [results, setResults] = useState({});
  const [loadingRepos, setLoadingRepos] = useState({});
  const [lastChecked, setLastChecked] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  const fetchAll = useCallback(async(prefix) => {
    setRefreshing(true);

    const linked = await GitLabAuth.isAuthenticated();
    setGitlabLinked(linked);

    if (!linked) {
      setRefreshing(false);
      return;
    }

    const nextLoading = {};
    REPOS.forEach((repo) => { nextLoading[repo] = true; });
    setLoadingRepos(nextLoading);

    await Promise.all(REPOS.map(async(repo) => {
      const projectId = `${prefix}/${repo}`;
      try {
        const [pipeline, vulnerabilities, sbom] = await Promise.all([
          opsStatusService.getLatestPipeline(projectId).catch(() => null),
          opsStatusService.getVulnerabilitySummary(projectId).catch(() => ({ available: false, counts: {}, total: 0 })),
          opsStatusService.getSbomStatus(projectId).catch(() => ({ available: false, count: 0 })),
        ]);

        setResults((prev) => ({
          ...prev,
          [repo]: { data: { pipeline, vulnerabilities, sbom }, error: null },
        }));
      } catch {
        setResults((prev) => ({
          ...prev,
          [repo]: { data: null, error: 'Failed to reach GitLab for this repo (check project path/permissions)' },
        }));
      } finally {
        setLoadingRepos((prev) => ({ ...prev, [repo]: false }));
      }
    }));

    setLastChecked(new Date());
    setRefreshing(false);
  }, []);

  useEffect(() => {
    fetchAll(groupPrefix);
    // Intentionally run once on mount only — refresh is manual (see button below).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleGroupPrefixBlur = () => {
    saveGroupPrefix(groupPrefix);
  };

  return (
    <div className="ops-status-page">
      <div className="ops-status-header">
        <div className="ops-status-title-row">
          <Icon color="#8B5CF6" glow={false} name="gitlab" size={22} />
          <h1 className="ops-status-title">Flow ecosystem status</h1>
        </div>
        <p className="ops-status-subtitle">
          Hidden ops page — CI pipeline status, Security Dashboard vulnerabilities, and SBOM/dependency availability
          for the Flow component repos. This is a fallback to GitLab&apos;s own Pipelines and Security Dashboard UI, not
          a replacement — no auto-refresh, no history.
        </p>

        <div className="ops-status-controls">
          <label className="ops-status-group-prefix-label" htmlFor="ops-status-group-prefix">
            GitLab group prefix
          </label>
          <input
            id="ops-status-group-prefix"
            onBlur={handleGroupPrefixBlur}
            onChange={(event) => setGroupPrefix(event.target.value)}
            type="text"
            value={groupPrefix}
          />
          <button
            className="ops-status-refresh-button"
            disabled={refreshing}
            onClick={() => fetchAll(groupPrefix)}
            type="button"
          >
            {refreshing ? 'Refreshing…' : 'Refresh'}
          </button>
          <span className="ops-status-last-checked">
            {lastChecked ? `Last checked ${lastChecked.toLocaleTimeString()}` : ''}
          </span>
        </div>
      </div>

      {gitlabLinked === false && (
        <div className="ops-status-not-linked">
          GitLab isn&apos;t connected for your account. Connect it from the{' '}
          <a href="/tools">Connectors page</a>, then come back and refresh.
        </div>
      )}

      {gitlabLinked !== false && (
        <div className="ops-status-grid">
          {REPOS.map((repo) => {
            const projectId = `${groupPrefix}/${repo}`;
            const entry = results[repo];
            return (
              <RepoCard
                data={entry?.data}
                error={entry?.error}
                key={repo}
                loading={!!loadingRepos[repo]}
                projectId={projectId}
                repo={repo}
              />
            );
          })}
        </div>
      )}
    </div>
  );
};

export default InternalStatus;
